import React, { memo, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  DateAndTimePicker,
  Icon,
  InlineMessage,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { addDays, addYears, format } from 'date-fns';

import { CF_NEU_BLD } from '../../../constants';
import { useInvertedLocalize } from '../../../hooks';

import { getUTCEpochFromLocalDate } from './utils';

import './TimeRangePicker.css';

type DurationType = 7 | 21 | 30 | 90 | 'custom';

interface DurationState {
  key: DurationType;
  label: string;
}

const CUSTOM_KEY = 'custom';

const DURATIONS: DurationState[] = [
  { label: '1 week', key: 7 },
  { label: '3 week', key: 21 },
  { label: '1 month', key: 30 },
  { label: '3 months', key: 90 },
  { label: 'Custom', key: CUSTOM_KEY },
];

interface TimeRangePickerProps {
  isCritical: boolean;
  setEpoch: React.Dispatch<
    React.SetStateAction<{
      startEpoch: number | null;
      endEpoch: number | null;
    }>
  >;
}

/**
 * TimeRangePicker
 */
function TimeRangePicker({ isCritical, setEpoch }: TimeRangePickerProps) {
  const l = useInvertedLocalize();

  //  1 => `Now`, 2 => `Future`
  const [timeMode, setTimeMode] = useState(1);
  const [timeRange, setTimeRange] = useState<string | number | null>(null);

  // Dates
  const [startDateTime, setStartDateTime] = useState<Date | null>(new Date());
  const [endDateTime, setEndDateTime] = useState<Date | null>(null);

  useEffect(() => {
    if (timeMode === 1) {
      setTimeout(() => {
        setStartDateTime(new Date());
      }, 400);
    }
  }, [timeMode]);

  useEffect(() => {
    if (timeRange === CUSTOM_KEY) {
      setEndDateTime(null);

      return;
    }

    if (typeof timeRange === 'number') {
      const updatedEndDateTime = startDateTime
        ? addDays(startDateTime, timeRange)
        : null;

      setEndDateTime(updatedEndDateTime);
    }
  }, [timeRange, startDateTime]);

  useEffect(() => {
    setEpoch({
      startEpoch: startDateTime
        ? getUTCEpochFromLocalDate(startDateTime)
        : null,
      endEpoch: endDateTime ? getUTCEpochFromLocalDate(endDateTime) : null,
    });
  }, [startDateTime, endDateTime, setEpoch]);

  // Shared max date
  const rangeMaxDate = addYears(new Date(), 999);

  const startDateTimePickerClasses = cx(
    'TimeRangePicker-timePicker--isStartTime',
    timeMode === 2 && 'TimeRangePicker-timePicker--isOpen'
  );
  const endDateTimePickerClasses = cx(
    'TimeRangePicker-timePicker--isEndTime',
    timeRange === 'custom' && 'TimeRangePicker-timePicker--isOpen'
  );

  return (
    <Box tag='div' className='TimeRangePicker'>
      <Typography kind='h3' color={CF_NEU_BLD}>
        {l(
          'When would you like to start the “In Review” period, and for how long?'
        )}
      </Typography>
      <Box className='TimeRangePicker-bodyWrapper'>
        <Box tag='div' className='TimeRangePicker-modetoggler'>
          <ToggleButtonGroup selection={timeMode} setSelection={setTimeMode}>
            <ToggleButton label={l('Now')} />
            <ToggleButton label={l('Future')} />
          </ToggleButtonGroup>
        </Box>
        <Box tag='div' className={startDateTimePickerClasses}>
          <Typography kind='h4' color={CF_NEU_BLD}>
            {l('Start Date')}
          </Typography>
          <DateAndTimePicker
            kind='dateAndTimeStart'
            label=''
            startDate={startDateTime}
            setStartDate={setStartDateTime}
            endDate={endDateTime}
            maxDate={rangeMaxDate}
            minDate={new Date()}
            timeMin={{
              minutes: Number(format(new Date(), 'mm')),
              hours: Number(format(new Date(), 'HH')),
            }}
            dateFormat='MM/dd/yyyy, h:mm aa'
            portalId='root'
          />
        </Box>
        <Box tag='div' className='TimeRangePicker-timeRangePicker'>
          <Typography kind='h4' color={CF_NEU_BLD}>
            {l('Duration')}
          </Typography>
          <Box tag='div' className='TimeRangePicker-timeRangePickerChips'>
            {DURATIONS.map((duration) => (
              <Chip
                key={duration.key}
                kind={timeRange === duration.key ? 'boldFilled' : 'outlined'}
                label={duration.label}
                onClick={() => setTimeRange(duration.key)}
                color='brandSecondary'
              />
            ))}
            <span className='TimeRangePicker-timeRangePickerDivider' />
          </Box>
        </Box>
        <Box tag='div' className={endDateTimePickerClasses}>
          <Typography kind='h4' color={CF_NEU_BLD}>
            {l('End Date')}
          </Typography>
          <DateAndTimePicker
            kind='dateAndTimeEnd'
            label=''
            startDate={startDateTime}
            setStartDate={setStartDateTime}
            endDate={endDateTime}
            setEndDate={setEndDateTime}
            maxDate={rangeMaxDate}
            minDate={new Date()}
            dateFormat='MM/dd/yyyy, h:mm aa'
            portalId='root'
          />
        </Box>
      </Box>
      {isCritical && (
        <InlineMessage
          text={l('This required field is incomplete')}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      )}
    </Box>
  );
}

export default memo(TimeRangePicker);
